/**
 * Shared bits for the Tasks feature — kept as a tiny standalone module so
 * the Board page (`Tasks.jsx`) and Inbox page (`TasksInbox.jsx`) can both
 * render the same status pills / priority dots / avatars without
 * duplicating ~80 lines of CSS-laden components.
 */
import React from 'react';
import {
  CircleDashed, Activity as ActivityIcon, CheckCircle2, Ban,
  User as UserIcon,
} from 'lucide-react';

export const STATUS_META = {
  todo: { label: 'To do', color: '#94A3B8', bg: '#F1F5F9', dot: '#64748B', icon: CircleDashed },
  in_progress: { label: 'In progress', color: '#5E1F8B', bg: '#F5E9FE', dot: '#8B3FC5', icon: ActivityIcon },
  blocked: { label: 'Blocked', color: '#B91C1C', bg: '#FEE2E2', dot: '#DC2626', icon: Ban },
  done: { label: 'Done', color: '#047857', bg: '#D1FAE5', dot: '#10B981', icon: CheckCircle2 },
};

export const STATUS_ORDER = ['todo', 'in_progress', 'blocked', 'done'];

export const PRIORITY_META = {
  low: { label: 'Low', color: '#64748B' },
  medium: { label: 'Medium', color: '#0891B2' },
  high: { label: 'High', color: '#D97706' },
  critical: { label: 'Critical', color: '#DC2626' },
};

export const PRIORITY_ORDER = ['low', 'medium', 'high', 'critical'];

export function initials(name = '') {
  const parts = (name || '').split(/[\s@.]/).filter(Boolean);
  return ((parts[0]?.[0] || '?') + (parts[1]?.[0] || '')).toUpperCase();
}

export function isOverdue(due_date, status) {
  if (!due_date || status === 'done') return false;
  return due_date < new Date().toISOString().slice(0, 10);
}

export function formatDueDate(d) {
  if (!d) return '';
  const today = new Date().toISOString().slice(0, 10);
  if (d === today) return 'Today';
  const diff = Math.round((new Date(d) - new Date(today)) / 86400000);
  if (diff === 1) return 'Tomorrow';
  if (diff === -1) return 'Yesterday';
  if (diff > 0 && diff < 7) return `In ${diff}d`;
  if (diff < 0) return `${-diff}d ago`;
  return d.slice(5);
}

export function Avatar({ name, size = 'md' }) {
  const sz = size === 'sm'
    ? 'h-5 w-5 text-[8px]'
    : size === 'lg'
      ? 'h-8 w-8 text-[10px]'
      : 'h-6 w-6 text-[9px]';
  return (
    <div
      className={`${sz} rounded-full bg-gradient-to-br from-[#8B3FC5] to-[#5E1F8B] text-white font-bold flex items-center justify-center shrink-0`}
      title={name || 'Unassigned'}
    >
      {name ? initials(name) : <UserIcon size={11} />}
    </div>
  );
}

export function StatusPill({ status, onClick, testid, size = 'md' }) {
  const meta = STATUS_META[status] || STATUS_META.todo;
  const Icon = meta.icon;
  const sizeCls = size === 'sm'
    ? 'px-1.5 py-0.5 text-[9px] gap-1'
    : 'px-2 py-1 text-[10px] gap-1.5';
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center rounded uppercase tracking-[0.16em] font-bold border transition-all hover:scale-[1.03] cursor-pointer ${sizeCls}`}
      style={{ background: meta.bg, color: meta.color, borderColor: meta.color + '55' }}
      title="Click to cycle status"
    >
      <Icon size={size === 'sm' ? 9 : 11} />
      {meta.label}
    </button>
  );
}

export function PriorityDot({ priority, onClick, testid }) {
  const meta = PRIORITY_META[priority] || PRIORITY_META.medium;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      title={`Priority: ${meta.label} — click to cycle`}
      className="inline-flex items-center gap-1.5 hover:scale-110 transition-transform"
    >
      <span className="h-2.5 w-2.5 rounded-full" style={{ background: meta.color, boxShadow: `0 0 0 2px ${meta.color}33` }} />
      <span className="text-[10px] uppercase tracking-[0.14em] font-bold" style={{ color: meta.color }}>
        {meta.label}
      </span>
    </button>
  );
}
